/**
 * Frontend-Berechtigungs-Hooks/-Helper. Spiegeln die geteilte Matrix aus
 * `@projektverwaltung/shared` (Single Source of Truth) auf den aktiven Session-User.
 * Das Backend erzwingt dieselben Regeln — hier nur fürs Button-/Action-Gating.
 */
import {
  type LifecycleStatus,
  type PermissionUser,
  type ProjectScopeShape,
  type Segment,
  canDoProjectAction,
  canResolveStaffing,
} from '@projektverwaltung/shared';
import { useSession } from './session.js';

/** Lockerer Projekt-Shape, wie ihn ein `Project` oder eine Board-Row liefert. */
type ProjectLike = {
  project_id: string;
  segment: string;
  project_lead_user_id?: string | null | undefined;
  manager_user_id?: string | null | undefined;
  mtb_user_id?: string | null | undefined;
  responsibility?: string | null | undefined;
  /**
   * Eingangsphase (#78). Hier **optional**, weil Board-Zeilen aus schlankeren Endpunkten
   * kommen und Aktionen pruefen, fuer die die Phase keine Rolle spielt.
   *
   * Nur `assign_lead_manager` haengt daran — und dafuer gibt es
   * `useCanAssignLeadManager`, das das Feld per Typ **verlangt**. Damit kann die einzige
   * Pruefung, die es braucht, nicht ohne es aufgerufen werden.
   */
  phase_is_intake?: boolean | undefined;
  /**
   * Lebenszyklus (#123) — bewusst **Pflicht**: `merged` sperrt jede Schreibaktion. Optional
   * wäre ein vergessenes Feld ein stilles „bearbeitbar" am zusammengeführten Projekt.
   */
  lifecycle_status: LifecycleStatus;
};

/**
 * Lebenszyklus aus einer schlanken Listen-Zeile (`ProjectSchema.partial()`), in der das Feld nur
 * typseitig optional ist — die Endpunkte liefern es immer (projectWithLeadSelect). Fehlt es
 * trotzdem, gilt „nur lesbar" (#123, fail-closed): ein stilles „aktiv" höbe den Schreibschutz auf.
 */
export function lebenszyklusOderNurLesbar(v: LifecycleStatus | null | undefined): LifecycleStatus {
  return v ?? 'merged';
}

/** Ein Projekt-Shape, das den Zuteilungsschutz beantworten kann. */
type ProjectWithPhaseFlag = ProjectLike & { phase_is_intake: boolean };

function projectScopeOf(p: ProjectLike): ProjectScopeShape {
  return {
    project_id: p.project_id,
    segment: p.segment as Segment,
    project_lead_user_id: p.project_lead_user_id ?? null,
    manager_user_id: p.manager_user_id ?? null,
    mtb_user_id: p.mtb_user_id ?? null,
    responsibility: (p.responsibility ?? null) as ProjectScopeShape['responsibility'],
    // Fehlt die Angabe, gilt „nicht in der Eingangsphase". Fuer alle anderen Aktionen ist der
    // Wert bedeutungslos; fuer `assign_lead_manager` kann er dank ProjectWithPhaseFlag gar
    // nicht fehlen. Das Backend entscheidet ohnehin erneut und mit der echten Phase.
    phase_is_intake: p.phase_is_intake ?? false,
    lifecycle_status: p.lifecycle_status,
  };
}

/** Pure Helper (z. B. für Board-Listen) — Bedarf am Projekt verwalten (anlegen/löschen/reopen). */
export function canManageStaffingFor(user: PermissionUser, project: ProjectLike): boolean {
  return canDoProjectAction(user, projectScopeOf(project), 'staffing_manage');
}

/**
 * Darf der aktive User den Einzelprojekt-Sync „Aus Salesforce neu laden" auslösen (#49)?
 * Segment-gebunden statt zuteilungsgebunden — Begründung siehe Matrix in `shared/permissions.ts`.
 */
export function useCanSyncProject(project: ProjectLike | null | undefined): boolean {
  const { user } = useSession();
  if (!user || !project) return false;
  return canDoProjectAction(user, projectScopeOf(project), 'sync');
}

/**
 * Staffing-Capabilities für ein konkretes Projekt:
 * - canResolve: zuweisen / besetzt / geplant (nur KL/Admin, projektunabhängig)
 * - canManage:  anlegen / löschen / wieder öffnen (Bereichsrollen im Scope)
 * - canAusplanen: Besetzung ausplanen (KFML/Admin + PL des Projekts, #158)
 */
export function useStaffingPermissions(project: ProjectLike | null | undefined): {
  canResolve: boolean;
  canManage: boolean;
  canAusplanen: boolean;
} {
  const { user } = useSession();
  if (!user) return { canResolve: false, canManage: false, canAusplanen: false };
  return {
    // #123 (Review S2): `canResolveStaffing` hängt nur an der Rolle. Ein zusammengeführtes Projekt
    // ist aber für jeden nur lesbar (Server: 409) — sonst zeigte es KL/Admin weiter
    // „Staffing abschließen", Zuweisen und „Wieder fällig am".
    canResolve: canResolveStaffing(user) && project?.lifecycle_status !== 'merged',
    canManage: project ? canManageStaffingFor(user, project) : false,
    // #158: Besetzung ausplanen — KFML/Admin und die PL des Projekts
    canAusplanen: project
      ? canDoProjectAction(user, projectScopeOf(project), 'besetzung_ausplanen')
      : false,
  };
}

/**
 * Darf der aktive Nutzer die Intake-/Übergabe-Felder ändern (`edit_intake`)? Wie der Server
 * (`updateProject`, `setzeUebergabeNotiz`) nur an aktiven Projekten — abgeschlossene sind
 * schreibgeschützt (409), zusammengeführte sperrt schon die Matrix. Genutzt von der
 * Übergabe-Notiz in der Staffing-Maske (#158).
 */
export function useCanEditIntake(project: ProjectLike | null | undefined): boolean {
  const { user } = useSession();
  if (!user || !project || project.lifecycle_status !== 'active') return false;
  return canDoProjectAction(user, projectScopeOf(project), 'edit_intake');
}

/**
 * Darf der aktive Nutzer Projektleiter und Manager zuteilen (#78)?
 *
 * In der Eingangsphase ist das der kaufmaennischen Leitung (und Voll-Admins) vorbehalten —
 * dort ist die Zuteilung fachlich noch nicht entschieden. Ausserhalb gilt das normale
 * Bearbeitungsrecht.
 *
 * Verlangt `phase_is_intake` im Projekt-Shape: Ohne diese Angabe laesst sich die Frage nicht
 * beantworten, und ein stillschweigendes „darf" waere die falsche Vermutung.
 *
 * Der MTB ist ausdruecklich NICHT betroffen — dafuer weiter `useCanEditProject`.
 */
export function useCanAssignLeadManager(project: ProjectWithPhaseFlag | null | undefined): boolean {
  const { user } = useSession();
  if (!user || !project) return false;
  return canDoProjectAction(user, projectScopeOf(project), 'assign_lead_manager');
}
